"use client";
import React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  FaWhatsapp,
  FaTelegram,
  FaTiktok,
  FaInstagram,
  FaFacebook,
  FaLinkedinIn,
} from "react-icons/fa";
import {
  MdOutlineEmail,
} from "react-icons/md";
import { BiPhoneCall } from "react-icons/bi";
import { useTranslation } from "react-i18next";
import { useSession } from "next-auth/react";
import Image from "next/image";
import Logo from "@/public/logo.png";
import { isMobile } from "react-device-detect";
import { useDrawer } from "@/utils/DrawerProvider";
import Terms from "../../app/[locale]/signup/Terms&Conditions";
import { handleCall, handleEmailClick } from "@/utils/functions";
import { IoLocationOutline } from "react-icons/io5";
import { UserSession } from "@/utils/types";

export const Footer = () => {
  const router = useRouter();
  const { t, i18n } = useTranslation(); // Translation hook
  const currentLocale = i18n.language as "ar" | "fr" | "en";
  const { openDrawer } = useDrawer(); // Custom drawer hook
  const session = useSession()?.data as UserSession; // Get session data

  // Open WhatsApp chat
  const handleWhatsappClick = (phoneNumber: any) => {
    const url = `https://wa.me/${phoneNumber}`;
    window.open(url, "_blank");
  };

  return (
    <div className="web- light-grey-bg">
      <div className={currentLocale == "ar" ? "footer rtl" : "footer"}>
        {/* Logo and social media icons */}
        <div>
          <div className=" footer-logo">
            <Image src={Logo} alt="" width={120} />
            <p>Amazigh group </p>
          </div>

          <div className="flex flex-wrap gap15">
            {/* Social links */}
            <a href="https://www.facebook.com/p/Amazigh-Group-100093226705788/?paipv=0" target="_blank" rel="noopener">
              <img src="/facebook.svg" alt="Facebook" width={30} />
            </a>
            <a href="https://www.instagram.com/amazighgroup/" target="_blank" rel="noopener">
              <img src="/instagram.svg" alt="Instagram" width={30} />
            </a>
            <a href="https://www.linkedin.com/company/amazigh-import/?trk=ppro_cprof" target="_blank" rel="noopener">
              <img src="/linkedin.svg" alt="LinkedIn" width={30} />
            </a>
            <a href="https://www.tiktok.com/@amazigh_group" target="_blank" rel="noopener">
              <img src="/tiktok.svg" alt="TikTok" width={30} />
            </a>
            <a href="https://t.me/amazighimportdz" target="_blank" rel="noopener">
              <img src="/telegram.svg" alt="Telegram" width={30} />
            </a>
            <a href="https://wa.me/0541920305" target="_blank" rel="noopener">
              <img src="/whatsapp.svg" alt="WhatsApp" width={30} />
            </a>
          </div>
        </div>

        {/* Contact Info */}
        <div>
          <div className="grid gap20">
            {/* Email */}
            <div className="auto-c1 gap10 text-hover vcenter pointer"
              onClick={() => handleEmailClick("contact@amazighgroupe.com")}
            >
              <MdOutlineEmail size={25} />
              <p className="text-hover f17">contact@amazighgroupe.com</p>
            </div>

            {/* WhatsApp */}
            <div className="auto-c1 gap10 text-hover vcenter pointer"
              onClick={() => handleWhatsappClick("+213541920305")}
            >
              <FaWhatsapp size={25} />
              <p className="text-hover f17">+213541920305 </p>
            </div>

            {/* Phone call */}
            <div className="auto-c1 gap10 text-hover vcenter pointer"
              onClick={() => handleCall("+213794106619 ")}
            >
              <BiPhoneCall size={25} />
              <p className="text-hover f17">+213794106619 </p>
            </div>

            {/* Address */}
            <div className="auto-c1 gap10 text-hover vcenter pointer">
              <IoLocationOutline size={25} />
              <p className="f17">{t("societyAddress")} </p>
            </div>
          </div>
        </div>

        {/* Navigation Links */}
        <div className="footer-links">
          <div className="grid gap30">
            <ul className="right c2 gap20 mc1">
              {/* First Column of Links */}
              <div className="grid gap20">
                <li className="text-hover f17">
                  <Link href="/">{t("home")} </Link>
                </li>
                <li className="text-hover f17">
                  <Link href="/import_china">{t("importChina")} </Link>
                </li>
                <li className="text-hover f17">
                  <Link href="/make-to-order">{t("Dubai Import")} </Link>
                </li>
                <li className="text-hover f17">
                  <Link href="/shop">{t("shop")}</Link>
                </li>
              </div>

              {/* Second Column of Links (Conditional by role) */}
              <div className="grid gap20 tright">
                <li className="text-hover f17">
                  <Link href="/about">{t("about")}</Link>
                </li>
                <li className="text-hover f17">
                  <Link href="/faq">{t("FAQ's")}</Link>
                </li>
                {session?.role === "user" && (
                  <li className="text-hover f17">
                    <Link href="/my_space?tab=orders">{t("mySpace")}</Link>
                  </li>
                )}
                {(session?.role === "admin" || session?.role === "super-admin") && (
                  <li className="text-hover f17">
                    <Link href="/admin">{t("mySpace")}</Link>
                  </li>
                )}
                {session?.role === "user" && (
                  <li className="text-hover f17">
                    <Link href="/profile">{t("profile")}</Link>
                  </li>
                )}
                {(session?.role === "admin" || session?.role === "super-admin") && (
                  <li className="text-hover f17">
                    <Link href="/admin/profile">{t("profile")}</Link>
                  </li>
                )}
              </div>
            </ul>

            {/* App download buttons */}
            <div className="c1-auto">
              <AppDownloadButtons />
            </div>
          </div>
        </div>
      </div>

      {/* Subfooter with Terms & Conditions */}
      <div className="sub-footer mc1">
        <p>{t("allRightsReserved")}</p>
        <div></div>
        <div
          onClick={() => openDrawer({ title: "", content: <Terms /> })}
          className="pointer tright"
        >
          <p className="light-blue">{t("terms")} </p>
        </div>
      </div>
    </div>
  );
};

// App Store & Google Play download buttons
const AppDownloadButtons = () => {
  return (
    <div className="c2 mc1 center gap10">
      <a href="https://apps.apple.com/app/idYOUR_APP_ID" target="_blank" rel="noopener">
        <img
          src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
          alt="Download on the App Store"
          height={50}
        />
      </a>
      <a href="https://play.google.com/store/apps/details?id=YOUR_APP_ID" target="_blank" rel="noopener">
        <img src="/googleplay.png" alt="Get it on Google Play" height={50} />
      </a>
    </div>
  );
};

export default AppDownloadButtons;
